import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  StandardDialog,
  StandardDialogContent,
  StandardDialogHeader,
  StandardDialogTitle,
  StandardDialogDescription,
  StandardDialogBody,
  StandardDialogFooter,
} from "@/components/ui/standard-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { TimezoneSelect } from "@/components/common/TimezoneSelect";
import { getBrowserTimeZone } from "@/lib/timezones";
import { useCreateOrganization } from "@/hooks/useCreateOrganization";
import {
  useChartOfAccountsTemplates,
  useApplyChartOfAccountsTemplate,
} from "@/hooks/useChartOfAccountsTemplates";
import { toast } from "@/hooks/useToast";
import { captureException } from "@/lib/sentry";
import { useTranslation } from "@/hooks/useTranslation";

// Radix Select items can't have an empty-string value, so "none" is the
// sentinel for "start with an empty chart of accounts".
const NO_TEMPLATE = "none";

const formSchema = z.object({
  name: z.string().min(1, "Organization name is required").max(100, "Organization name too long"),
  description: z.string().optional(),
  timezone: z.string().min(1, "Timezone is required"),
  template_id: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface CreateOrganizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOrganizationCreated?: (orgId: string) => void;
}

export function CreateOrganizationDialog({
  open,
  onOpenChange,
  onOrganizationCreated
}: Readonly<CreateOrganizationDialogProps>) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const createOrganization = useCreateOrganization();
  const { data: templates = [], isLoading: templatesLoading } = useChartOfAccountsTemplates();
  const applyTemplate = useApplyChartOfAccountsTemplate();

  // The selection is derived, not synced via effect: an unset field
  // (undefined) means "use the system default once templates load", while an
  // explicit 'none' means the user chose to start empty — so a deliberate
  // Empty pick made before the (async) templates resolve is never overwritten.
  const defaultTemplate = templates.find((tpl) => tpl.is_default);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      // Prefilled, visible, and required — the creator confirms or changes it.
      timezone: getBrowserTimeZone(),
      template_id: undefined,
    },
  });

  const onSubmit = async (data: FormData) => {
    if (!user) {
      toast.error(t('organizations.mustBeLoggedIn'));
      return;
    }

    try {
      const result = await createOrganization.mutateAsync({
        name: data.name,
        timezone: data.timezone,
        description: data.description,
      });

      // Apply the chosen chart-of-accounts template in its own try/catch:
      // the org already exists, so a template failure must not fail the flow.
      const chosenTemplateId = data.template_id ?? defaultTemplate?.id;
      if (chosenTemplateId && chosenTemplateId !== NO_TEMPLATE) {
        try {
          await applyTemplate.mutateAsync({
            organizationId: result.organizationId,
            templateId: chosenTemplateId,
          });
        } catch (applyError) {
          // Show the real refusal (e.g. a subtype with no house code) — a
          // generic warning hid why the chart came out empty.
          captureException(applyError, { context: 'CreateOrganizationDialog.applyTemplate' });
          toast.error(
            t('organizations.coaTemplate.applyFailed', {
              error: applyError instanceof Error ? applyError.message : String(applyError),
            }),
          );
        }
      }

      toast.success(t('organizations.createSuccess'));
      onOpenChange(false);
      form.reset();
      onOrganizationCreated?.(result.organizationId);
    } catch (error) {
      console.error('Error creating organization:', error);
      toast.error(t('organizations.createFailed') + (error instanceof Error ? ': ' + error.message : ''));
    }
  };

  const isPending = createOrganization.isPending || applyTemplate.isPending;

  return (
    <StandardDialog open={open} onOpenChange={onOpenChange}>
      <StandardDialogContent size="lg">
        <StandardDialogHeader>
          <StandardDialogTitle>{t('organizations.createNew')}</StandardDialogTitle>
          <StandardDialogDescription>
            {t('organizations.createDescription')}
          </StandardDialogDescription>
        </StandardDialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="contents">
            {/* min-w-0: the timezone trigger's long nowrap label (e.g.
                Europe/Amsterdam + cities) would otherwise force the body wider
                than the dialog, dragging every input past its edge. */}
            <StandardDialogBody className="min-w-0 space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel>{t('organizations.name')}</FormLabel>
                      {fieldState.error && (
                        <span className="text-xs text-destructive">{fieldState.error.message}</span>
                      )}
                    </div>
                    <FormControl>
                      <Input
                        placeholder={t('organizations.namePlaceholder')}
                        className={fieldState.error ? "border-destructive" : ""}
                        {...field}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('organizations.description')}</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t('organizations.descriptionPlaceholder')}
                        className="resize-none"
                        rows={3}
                        {...field}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="timezone"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel>{t('organizations.timezone.label')}</FormLabel>
                      {fieldState.error && (
                        <span className="text-xs text-destructive">{fieldState.error.message}</span>
                      )}
                    </div>
                    <FormControl>
                      <TimezoneSelect
                        value={field.value}
                        onValueChange={field.onChange}
                      />
                    </FormControl>
                    <p className="text-sm text-muted-foreground">
                      {t('organizations.timezone.help')}
                    </p>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="template_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('organizations.coaTemplate.label')}</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value ?? defaultTemplate?.id ?? NO_TEMPLATE}
                        onValueChange={field.onChange}
                        disabled={templatesLoading}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('organizations.coaTemplate.placeholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NO_TEMPLATE}>
                            {t('organizations.coaTemplate.empty')}
                          </SelectItem>
                          {templates.map((tpl) => (
                            <SelectItem key={tpl.id} value={tpl.id}>
                              {tpl.name}
                              {tpl.is_default ? ` (${t('organizations.coaTemplate.defaultBadge')})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <p className="text-sm text-muted-foreground">
                      {t('organizations.coaTemplate.help')}
                    </p>
                  </FormItem>
                )}
              />

            </StandardDialogBody>

            <StandardDialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {isPending ? t('loading') : t('organizations.create')}
              </Button>
            </StandardDialogFooter>
          </form>
        </Form>
      </StandardDialogContent>
    </StandardDialog>
  );
}
